import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Field } from '../components/ui/field';
import { Input, Select, Textarea } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';
import { formatDateTime } from '../lib/utils';

const FIELDS = [
  'shortDescription',
  'description',
  'seoTitle',
  'seoDescription',
  'faq',
  'translation',
] as const;
const LOCALES = ['lv', 'lt', 'et', 'ru', 'en'] as const;
type Status = 'pending' | 'approved' | 'rejected';

interface Draft {
  id: string;
  entityId: string;
  productName: string | null;
  field: (typeof FIELDS)[number];
  locale: string | null;
  currentValue: string | null;
  proposedValue: string;
  model: string;
  status: Status;
  createdAt: string;
  reviewedAt: string | null;
}

/** JSON (FAQ, перевод) показываем читаемо, текст — как есть. */
const pretty = (value: string | null) => {
  if (!value) return '';
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

function DraftCard({ draft }: { draft: Draft }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [value, setValue] = useState(pretty(draft.proposedValue));
  const [error, setError] = useState<string>();
  const act = async (action: 'approve' | 'reject') => {
    setError(undefined);
    try {
      const edited = value !== pretty(draft.proposedValue);
      await api(`/ai/content/drafts/${draft.id}/${action}`, {
        method: 'POST',
        body: action === 'approve' && edited ? { value } : {},
      });
      await queryClient.invalidateQueries({ queryKey: ['ai', 'drafts'] });
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  return (
    <Card data-testid="ai-draft">
      <CardHeader
        title={draft.productName ?? draft.entityId}
        description={`${t(`drafts.fields.${draft.field}`)} · ${draft.locale ?? ''} · ${draft.model} · ${formatDateTime(draft.createdAt, i18n.language)}`}
        actions={
          <Link
            to="/products/$productId"
            params={{ productId: draft.entityId }}
            className="text-sm text-primary hover:underline"
          >
            {t('drafts.openProduct')}
          </Link>
        }
      />
      <CardContent className="grid gap-3 text-sm md:grid-cols-2">
        <div>
          <p className="mb-1 text-xs text-muted-foreground">{t('drafts.current')}</p>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 font-sans">
            {pretty(draft.currentValue) || '—'}
          </pre>
        </div>
        <div>
          <p className="mb-1 text-xs text-muted-foreground">{t('drafts.proposed')}</p>
          {draft.status === 'pending' ? (
            <Textarea
              aria-label={t('drafts.proposed')}
              rows={Math.min(14, Math.max(3, value.split('\n').length + 1))}
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
              }}
            />
          ) : (
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 font-sans">
              {value}
            </pre>
          )}
        </div>
        {draft.status === 'pending' ? (
          <div className="flex gap-2 md:col-span-2">
            <Button onClick={() => void act('approve')}>{t('drafts.approve')}</Button>
            <Button variant="outline" onClick={() => void act('reject')}>
              {t('drafts.reject')}
            </Button>
          </div>
        ) : null}
        {error ? (
          <Alert variant="destructive" className="md:col-span-2">
            {error}
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Черновики AI (§15 п. 21): проверка, правка и публикация в карточку товара. */
export function AiDraftsPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>('pending');
  const [field, setField] = useState<(typeof FIELDS)[number]>('seoDescription');
  const [locale, setLocale] = useState<string>('lv');
  const [sourceLocale, setSourceLocale] = useState<string>('lv');
  const [limit, setLimit] = useState(20);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const drafts = useQuery({
    queryKey: ['ai', 'drafts', status],
    queryFn: () => api<{ items: Draft[] }>(`/ai/content/drafts?status=${status}`),
  });

  const fillGaps = async () => {
    setError(undefined);
    setMessage(undefined);
    try {
      await api('/ai/content/generate-missing', {
        method: 'POST',
        body: { field, locale, limit, ...(field === 'translation' ? { sourceLocale } : {}) },
      });
      setMessage(t('drafts.queued'));
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <>
      <PageHeader title={t('drafts.title')} description={t('drafts.hint')} />
      <div className="grid gap-6">
        <Card>
          <CardHeader title={t('drafts.bulk')} description={t('drafts.bulkHint')} />
          <CardContent className="flex flex-wrap items-end gap-3 text-sm">
            <Field label={t('drafts.field')}>
              {(id) => (
                <Select
                  id={id}
                  value={field}
                  onChange={(e) => {
                    setField(e.target.value as (typeof FIELDS)[number]);
                  }}
                >
                  {FIELDS.map((f) => (
                    <option key={f} value={f}>
                      {t(`drafts.fields.${f}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t('drafts.language')}>
              {(id) => (
                <Select
                  id={id}
                  value={locale}
                  onChange={(e) => {
                    setLocale(e.target.value);
                  }}
                >
                  {LOCALES.map((l) => (
                    <option key={l}>{l}</option>
                  ))}
                </Select>
              )}
            </Field>
            {field === 'translation' ? (
              <Field label={t('drafts.from')}>
                {(id) => (
                  <Select
                    id={id}
                    value={sourceLocale}
                    onChange={(e) => {
                      setSourceLocale(e.target.value);
                    }}
                  >
                    {LOCALES.map((l) => (
                      <option key={l}>{l}</option>
                    ))}
                  </Select>
                )}
              </Field>
            ) : null}
            <Field label={t('drafts.limit')}>
              {(id) => (
                <Input
                  id={id}
                  type="number"
                  min={1}
                  max={500}
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                  }}
                  className="w-24"
                />
              )}
            </Field>
            <Button onClick={() => void fillGaps()}>{t('drafts.fillGaps')}</Button>
            {message ? <Alert variant="success">{message}</Alert> : null}
            {error ? <Alert variant="destructive">{error}</Alert> : null}
          </CardContent>
        </Card>

        <div className="flex gap-2" role="tablist">
          {(['pending', 'approved', 'rejected'] as const).map((s) => (
            <Button
              key={s}
              size="sm"
              role="tab"
              aria-selected={s === status}
              variant={s === status ? 'default' : 'outline'}
              onClick={() => {
                setStatus(s);
              }}
            >
              {t(`drafts.status.${s}`)}
              {s === status && drafts.data ? (
                <Badge variant="secondary" className="ml-2">
                  {drafts.data.items.length}
                </Badge>
              ) : null}
            </Button>
          ))}
        </div>
        {drafts.data?.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('drafts.empty')}</p>
        ) : null}
        {(drafts.data?.items ?? []).map((d) => (
          <DraftCard key={d.id} draft={d} />
        ))}
      </div>
    </>
  );
}

/** Кнопка в карточке товара: черновики AI для выбранного языка. */
export function AiDraftButton({
  productId,
  locale,
  hasName,
}: {
  productId: string;
  locale: string;
  hasName: boolean;
}) {
  const { t } = useTranslation();
  const [message, setMessage] = useState<string>();
  const [busy, setBusy] = useState(false);
  const generate = async () => {
    setBusy(true);
    setMessage(undefined);
    try {
      const result = await api<{ created: unknown[]; skipped: { reason: string }[] }>(
        '/ai/content/generate',
        {
          method: 'POST',
          body: {
            productId,
            locale,
            fields: hasName
              ? ['shortDescription', 'description', 'seoTitle', 'seoDescription', 'faq']
              : ['translation'],
            ...(hasName ? {} : { sourceLocale: locale === 'lv' ? 'en' : 'lv' }),
          },
        },
      );
      setMessage(
        t('drafts.created', { count: result.created.length }) +
          (result.skipped.length
            ? ` ${t('drafts.skipped', { count: result.skipped.length })}`
            : ''),
      );
    } catch (err) {
      setMessage(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="flex items-center gap-2 text-sm">
      {message ? (
        <Link to="/ai/drafts" className="text-primary hover:underline">
          {message}
        </Link>
      ) : null}
      <Button size="sm" variant="outline" disabled={busy} onClick={() => void generate()}>
        {hasName ? t('drafts.generate') : t('drafts.translate')} ({locale})
      </Button>
    </span>
  );
}
